import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { FileText, Calendar, ArrowRight } from "lucide-react";
import Card from "../components/Card";
import EmptyState from "../components/EmptyState";
import LoadingSkeleton from "../components/LoadingSkeleton";
import type { Call } from "../types";

export default function InsightsPage() {
  const navigate = useNavigate();
  const [completedCalls, setCompletedCalls] = useState<Call[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCalls();
  }, []);

  const loadCalls = async () => {
    try {
      const response = await fetch("/api/calls");
      if (response.ok) {
        const calls = await response.json();
        const withInsights = calls.filter(
          (c: Call) =>
            c.status === "completed" ||
            c.transcription ||
            c.followUpEmail ||
            c.prepPhase?.status === "completed"
        );
        setCompletedCalls(
          withInsights.sort(
            (a: Call, b: Call) =>
              new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime()
          )
        );
      }
    } catch (error) {
      console.error("Error loading calls:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingSkeleton count={4} />;
  }

  if (completedCalls.length === 0) {
    return (
      <EmptyState
        icon="lightbulb"
        title="No insights yet"
        description="Complete some calls to see AI-generated insights, transcriptions, and follow-up emails here."
        actionLabel="View Your Calls"
        onAction={() => navigate("/calls")}
      />
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-slate-900">Insights</h2>

      <div className="space-y-4">
        {completedCalls.map((call) => (
          <Card
            key={call.id}
            onClick={() => navigate(`/calls/${call.id}`)}
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-slate-900">{call.clientName}</h3>
                <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {format(new Date(call.scheduledAt), "MMM d, yyyy")}
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400" />
            </div>

            <div className="flex flex-wrap gap-2">
              {call.transcription && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full">
                  <FileText className="w-3 h-3" />
                  Transcription
                </span>
              )}
              {call.followUpEmail && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                  Follow-up Ready
                </span>
              )}
              {call.prepPhase?.status === "completed" && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full">
                  Fully Prepared
                </span>
              )}
              {call.aiBrief && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">
                  AI Brief
                </span>
              )}
            </div>

            {call.prepPhase?.prepSummary && (
              <p className="text-sm text-slate-600 mt-3 line-clamp-2">
                {call.prepPhase.prepSummary.substring(0, 150)}...
              </p>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
